import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KEY = "abn_cart_id";
const CartCtx = createContext(null);

export function money(m, lang = "it") {
    if (!m) return "";
    return new Intl.NumberFormat(lang === "en" ? "en-GB" : "it-IT", { style: "currency", currency: m.currencyCode || "EUR", maximumFractionDigits: 2 }).format(Number(m.amount));
}

const SIZE_ORDER = ["xxs", "xs", "s", "m", "l", "xl", "2xl", "xxl", "3xl", "xxxl", "4xl", "5xl"];
export function sortSizes(values) {
    return [...values].sort((a, b) => {
        const ia = SIZE_ORDER.indexOf(String(a).toLowerCase()), ib = SIZE_ORDER.indexOf(String(b).toLowerCase());
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
}

export function CartProvider({ children }) {
    const [cart, setCart] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const apply = useCallback((c) => {
        setCart(c);
        if (c?.id) localStorage.setItem(KEY, c.id); else localStorage.removeItem(KEY);
    }, []);

    useEffect(() => {
        const id = localStorage.getItem(KEY);
        if (!id) return;
        api.post("/shop/cart/get", { cartId: id }).then((r) => apply(r.data)).catch(() => apply(null));
    }, [apply]);

    const run = useCallback(async (fn, okMsg) => {
        setBusy(true);
        try { const r = await fn(); apply(r.data); if (okMsg) toast.success(okMsg, { position: "bottom-center" }); return true; }
        catch (e) { toast.error(e.response?.data?.detail || "Errore carrello"); return false; }
        finally { setBusy(false); }
    }, [apply]);

    const add = useCallback(async (merchandiseId, quantity = 1, okMsg) => {
        const id = cart?.id || localStorage.getItem(KEY);
        const ok = await run(() => id
            ? api.post("/shop/cart/add", { cartId: id, lines: [{ merchandiseId, quantity }] })
            : api.post("/shop/cart/create", { lines: [{ merchandiseId, quantity }] }), okMsg);
        if (ok) setOpen(true);
        return ok;
    }, [cart, run]);

    const update = useCallback((lineId, quantity) => run(() => api.post("/shop/cart/update", { cartId: cart.id, lines: [{ id: lineId, quantity }] })), [cart, run]);
    const remove = useCallback((lineId) => run(() => api.post("/shop/cart/remove", { cartId: cart.id, lineIds: [lineId] })), [cart, run]);

    const value = useMemo(() => ({
        cart, open, busy, setOpen, add, update, remove,
        count: cart?.totalQuantity || 0,
        lines: cart?.lines?.nodes || [],
    }), [cart, open, busy, add, update, remove]);
    return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
